/**
 * The places a user can pick for where they want to work, and the spellings postings use for each. The countries are
 * every one with a meaningful number of open jobs in the engine's data, each with its biggest job cities; the US has
 * its states and main tech cities too.
 */

export type PlaceKind = "country" | "state" | "city";

export interface Place {
  /** What the picker shows and the profile saves: "San Francisco, CA", "London, UK", "Germany". */
  label: string;
  kind: PlaceKind;
  /** Whole-word spellings that name it in a posting, as `placeName` writes them. */
  forms: string[];
  /** Upper-case tokens that name it: a state's postal code, a country's ISO code where no state has it. */
  codes: string[];
  /** A country's two-letter ISO code, even one a state shares: "Toronto, CA" is in Canada. */
  iso?: string;
  /** The country it is in, by label (a country's own label). */
  country: string;
  /** A US city's state, by label. */
  state?: string;
  /** Its place in its own list, busiest first: countries by open jobs, and each state's or country's cities. */
  rank: number;
}

/** Lower case, accents and punctuation dropped: "São Paulo" is "sao paulo", "D.C." is "d c". */
export function placeName(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const US = "United States";

const STATES: [name: string, code: string, ...forms: string[]][] = [
  ["Alabama", "AL"], ["Alaska", "AK"], ["Arizona", "AZ"], ["Arkansas", "AR"], ["California", "CA"], ["Colorado", "CO"],
  ["Connecticut", "CT"], ["Delaware", "DE"], ["Florida", "FL"], ["Georgia", "GA"], ["Hawaii", "HI"], ["Idaho", "ID"],
  ["Illinois", "IL"], ["Indiana", "IN"], ["Iowa", "IA"], ["Kansas", "KS"], ["Kentucky", "KY"], ["Louisiana", "LA"],
  ["Maine", "ME"], ["Maryland", "MD"], ["Massachusetts", "MA"], ["Michigan", "MI"], ["Minnesota", "MN"],
  ["Mississippi", "MS"], ["Missouri", "MO"], ["Montana", "MT"], ["Nebraska", "NE"], ["Nevada", "NV"],
  ["New Hampshire", "NH"], ["New Jersey", "NJ"], ["New Mexico", "NM"], ["New York", "NY"], ["North Carolina", "NC"],
  ["North Dakota", "ND"], ["Ohio", "OH"], ["Oklahoma", "OK"], ["Oregon", "OR"], ["Pennsylvania", "PA"],
  ["Rhode Island", "RI"], ["South Carolina", "SC"], ["South Dakota", "SD"], ["Tennessee", "TN"], ["Texas", "TX"],
  ["Utah", "UT"], ["Vermont", "VT"], ["Virginia", "VA"], ["Washington", "WA"], ["West Virginia", "WV"],
  ["Wisconsin", "WI"], ["Wyoming", "WY"],
  ["Washington, DC", "DC", "district of columbia", "washington dc", "washington d c", "dc washington", "d c"],
];

/** Label, the short name its cities' labels end with, upper-case codes, then other spellings: regions name their country too. */
const COUNTRIES: [label: string, short: string, codes: string, ...forms: string[]][] = [
  [US, "US", "US USA", "usa", "u s", "u s a", "united states of america"],
  ["United Kingdom", "UK", "GB GBR", "uk", "u k", "great britain", "britain", "england", "scotland", "wales", "northern ireland"],
  ["India", "India", "IN IND", "karnataka", "maharashtra", "tamil nadu", "telangana", "haryana", "uttar pradesh"],
  ["Canada", "Canada", "CA ON QC BC AB", "ontario", "quebec", "british columbia", "alberta", "nova scotia", "manitoba", "saskatchewan", "new brunswick"],
  ["China", "China", "CN CHN", "china mainland", "mainland china", "prc", "jiangsu", "guangdong", "zhejiang"],
  ["France", "France", "FR", "ile de france"],
  ["Germany", "Germany", "DE DEU", "deutschland", "bavaria", "bayern", "baden wurttemberg", "nrw"],
  ["Australia", "Australia", "AU NSW QLD VIC", "aus", "new south wales", "nsw", "queensland", "qld", "western australia", "south australia", "australian capital territory"],
  ["Spain", "Spain", "ES ESP", "espana", "catalonia", "comunidad de madrid"],
  ["Malaysia", "Malaysia", "MY MYS", "selangor"],
  ["Japan", "Japan", "JP JPN", "kanagawa"],
  ["Vietnam", "Vietnam", "VN", "viet nam"],
  ["Brazil", "Brazil", "BR", "brasil"],
  ["Ireland", "Ireland", "IE IRL"],
  ["Singapore", "Singapore", "SG SGP"],
  ["United Arab Emirates", "UAE", "AE", "uae", "u a e"],
  ["Netherlands", "Netherlands", "NL NLD", "the netherlands", "nederland"],
  ["Poland", "Poland", "PL POL", "polska"],
  ["Portugal", "Portugal", "PT PRT"],
  ["Mexico", "Mexico", "MX MEX", "jalisco", "nuevo leon"],
  ["Hungary", "Hungary", "HU HUN"],
  ["Philippines", "Philippines", "PH PHL", "metro manila"],
  ["Romania", "Romania", "RO ROU"],
  ["Colombia", "Colombia", "CO COL"],
  ["Italy", "Italy", "IT ITA", "italia"],
  ["Taiwan", "Taiwan", "TW TWN"],
  ["Thailand", "Thailand", "TH THA"],
  ["Belgium", "Belgium", "BE BEL", "belgique", "belgie"],
  ["Switzerland", "Switzerland", "CH CHE", "schweiz", "suisse"],
  ["Saudi Arabia", "Saudi Arabia", "KSA", "saudi", "kingdom of saudi arabia"],
  ["Austria", "Austria", "AT AUT", "osterreich"],
  ["South Korea", "South Korea", "KR KOR", "korea", "republic of korea"],
  ["Hong Kong", "Hong Kong", "HK HKG", "hong kong sar", "hksar"],
  ["Sweden", "Sweden", "SE SWE", "sverige"],
  ["Turkey", "Turkey", "TR TUR", "turkiye"],
  ["Chile", "Chile", "CL CHL"],
  ["Israel", "Israel", "IL ISR"],
  ["Indonesia", "Indonesia", "ID IDN"],
  ["South Africa", "South Africa", "ZA ZAF"],
  ["Serbia", "Serbia", "RS SRB", "srbija"],
  ["Denmark", "Denmark", "DK DNK", "danmark"],
  ["New Zealand", "New Zealand", "NZ NZL", "aotearoa"],
  ["Argentina", "Argentina", "AR ARG"],
  ["Bulgaria", "Bulgaria", "BG BGR"],
  ["Peru", "Peru", "PE PER"],
  ["Costa Rica", "Costa Rica", "CR CRI"],
  ["Czech Republic", "Czech Republic", "CZ CZE", "czechia"],
  ["Norway", "Norway", "NO NOR"],
  ["Greece", "Greece", "GR GRC"],
  ["Croatia", "Croatia", "HR HRV"],
  ["Qatar", "Qatar", "QA QAT"],
];

/** "Name|other spelling|…" by state code or country short name, the busiest first. */
const CITIES: Record<string, string[]> = {
  CA: [
    "San Francisco|sf|san francisco bay area|sf bay area|bay area", "Santa Clara", "Costa Mesa", "San Diego", "San Jose", "Hawthorne",
    "El Segundo", "Sunnyvale", "Los Angeles", "San Mateo", "Mountain View", "Irvine", "Palo Alto", "Long Beach", "Foster City",
    "Milpitas", "Menlo Park", "Sacramento", "Pleasanton", "Fremont", "Oakland", "Santa Monica", "Redwood City", "Culver City",
    "Pasadena", "Berkeley", "Cupertino",
  ],
  NY: ["New York|nyc|new york city|brooklyn|manhattan", "Buffalo"],
  TX: ["Austin", "Houston", "Dallas", "Plano", "San Antonio", "Richardson"],
  VA: ["McLean|mc lean", "Chantilly", "Arlington", "Richmond", "Reston", "Herndon"],
  WA: ["Seattle", "Redmond", "Bellevue", "Kirkland"],
  IL: ["Chicago"],
  MA: ["Boston", "Waltham", "Cambridge"],
  AL: ["Huntsville"],
  AZ: ["Tucson", "Phoenix", "Chandler", "Scottsdale", "Tempe"],
  GA: ["Atlanta"],
  ID: ["Boise"],
  CO: ["Denver", "Colorado Springs", "Boulder"],
  TN: ["Memphis", "Nashville"],
  PA: ["State College|university park|penn state university park", "Philadelphia", "Pittsburgh"],
  MD: ["Baltimore", "Bethesda"],
  FL: ["Tampa", "Miami", "Jacksonville", "Orlando"],
  NM: ["Albuquerque"],
  MN: ["Minneapolis"],
  UT: ["Salt Lake City", "Lehi"],
  NC: ["Charlotte", "Raleigh", "Durham"],
  OR: ["Hillsboro", "Portland"],
  OK: ["Oklahoma City"],
  NV: ["Las Vegas"],
  IN: ["Indianapolis"],
  HI: ["Honolulu"],
  MO: ["St. Louis|st louis|saint louis", "Kansas City"],
  OH: ["Cincinnati", "Columbus"],
  MI: ["Ann Arbor", "Detroit"],
  WI: ["Madison"],
  UK: ["London|greater london", "Bristol", "Manchester", "Glasgow", "Edinburgh", "Birmingham", "Cambridge", "Leeds", "Belfast"],
  India: ["Bangalore|bengaluru", "Hyderabad", "Pune", "Gurgaon|gurugram", "Chennai", "Mumbai|bombay", "Noida", "Coimbatore", "New Delhi|delhi"],
  Canada: ["Toronto", "Montreal", "Vancouver", "Ottawa", "Mississauga", "Calgary", "Edmonton", "Markham", "Waterloo"],
  China: ["Shanghai", "Suzhou", "Wuxi", "Beijing", "Shenzhen", "Guangzhou", "Hangzhou"],
  France: ["Paris", "Lyon", "Grenoble"],
  Germany: ["Berlin", "Munich|munchen|muenchen", "Stuttgart", "Hamburg", "Dresden", "Frankfurt"],
  Australia: ["Sydney", "Melbourne", "Canberra", "Perth", "Adelaide", "Brisbane"],
  Spain: ["Madrid", "Barcelona"],
  Malaysia: ["Penang|pulau pinang|bayan lepas|batu kawan", "Kuala Lumpur", "Petaling Jaya"],
  Japan: ["Tokyo", "Yokohama", "Osaka"],
  Vietnam: ["Ho Chi Minh City|ho chi minh|hcmc|saigon|thanh pho ho chi minh", "Hanoi|ha noi"],
  Brazil: ["Sao Paulo", "Campinas"],
  Ireland: ["Dublin|county dublin", "Cork", "Limerick"],
  UAE: ["Abu Dhabi", "Dubai"],
  Netherlands: ["Amsterdam", "Rotterdam", "Eindhoven"],
  Poland: ["Warsaw|warszawa", "Krakow|cracow", "Wroclaw", "Gdansk"],
  Portugal: ["Lisbon|lisboa", "Braga", "Porto"],
  Mexico: ["Mexico City|cdmx|ciudad de mexico", "Guadalajara", "Monterrey"],
  Hungary: ["Budapest"],
  Philippines: ["Taguig|city of taguig|bonifacio global city", "Manila", "Quezon City|quezon", "Cebu City|cebu"],
  Romania: ["Bucharest|bucuresti", "Cluj-Napoca|cluj napoca|cluj"],
  Colombia: ["Bogota", "Medellin"],
  Italy: ["Milan|milano", "Rome|roma"],
  Taiwan: ["Taichung", "Taipei|taipei city", "Hsinchu", "New Taipei|new taipei city|sanchong"],
  Thailand: ["Bangkok"],
  Belgium: ["Brussels|bruxelles"],
  Switzerland: ["Zurich"],
  "Saudi Arabia": ["Riyadh"],
  Austria: ["Vienna|wien"],
  "South Korea": ["Seoul"],
  Sweden: ["Stockholm"],
  Turkey: ["Istanbul"],
  Chile: ["Santiago"],
  Israel: ["Tel Aviv|tel aviv yafo", "Haifa"],
  Indonesia: ["Jakarta", "Semarang"],
  "South Africa": ["Johannesburg"],
  Serbia: ["Belgrade|beograd"],
  Denmark: ["Copenhagen|kobenhavn"],
  "New Zealand": ["Auckland"],
  Argentina: ["Buenos Aires"],
  "Czech Republic": ["Prague|praha"],
  Norway: ["Oslo"],
  Greece: ["Athens"],
};

const stateCodes = new Set(STATES.map(([, code]) => code));
const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))];

function build(): Place[] {
  const countries: Place[] = COUNTRIES.map(([label, , codes, ...forms], rank) => ({
    label,
    kind: "country",
    forms: uniq([placeName(label), ...forms]),
    codes: codes.split(" ").filter((c) => label === US || !stateCodes.has(c)),
    iso: codes.slice(0, 2),
    country: label,
    rank,
  }));
  const states: Place[] = STATES.map(([name, code, ...forms], rank) => ({ label: name, kind: "state", forms: uniq([placeName(name), ...forms]), codes: [code], country: US, rank }));
  const shortOf = new Map(COUNTRIES.map(([label, short]) => [short, label]));
  const stateOf = new Map(STATES.map(([name, code]) => [code, name]));
  const cities: Place[] = Object.entries(CITIES).flatMap(([where, names]) =>
    names.map((spec, rank) => {
      const [name, ...forms] = spec.split("|") as [string, ...string[]];
      const state = stateOf.get(where);
      return {
        label: `${name}, ${where}`,
        kind: "city" as const,
        forms: uniq([placeName(name), ...forms]),
        codes: [],
        country: state ? US : shortOf.get(where)!,
        ...(state ? { state } : {}),
        rank,
      };
    }),
  );
  return [...countries, ...states, ...cities];
}

/** Every place a user can pick: countries busiest first, then states, then cities. */
export const PLACES: readonly Place[] = build();

const byLabel = new Map(PLACES.map((p) => [p.label.toLowerCase(), p]));
const byForm = new Map<string, Place>();
const allByForm = new Map<string, Place[]>();
for (const p of PLACES) for (const f of p.forms) {
  if (!byForm.has(f)) byForm.set(f, p);
  allByForm.set(f, [...(allByForm.get(f) ?? []), p]);
}
const byCode = new Map(PLACES.flatMap((p) => p.codes.map((c) => [c, p] as const)));

const exactly = (v: string) => byLabel.get(v.toLowerCase()) ?? byForm.get(placeName(v)) ?? byCode.get(v.toUpperCase());

/** Whether a part after a city's comma is the city's own state or country: "TX", "Texas", "Canada", "CA" for Toronto. */
const within = (p: Place, part: string) => {
  const q = exactly(part);
  const up = part.toUpperCase();
  return (!!q && (q === p || q.label === p.country || q.label === p.state)) || up === byLabel.get(p.country.toLowerCase())?.iso || up === p.label.slice(p.label.lastIndexOf(",") + 1).trim().toUpperCase();
};

/**
 * The listed place a saved value names: its label, or for a value saved before the list, a spelling or code of one, or a
 * city followed by its own state or country. "Dublin, CA" and "Portland, ME" name no listed place.
 */
export function findPlace(value: string): Place | undefined {
  const v = value.trim();
  const whole = exactly(v);
  if (whole) return whole;
  const [head = "", ...rest] = v.split(",").map((s) => s.trim());
  const parts = rest.filter(Boolean);
  return parts.length ? allByForm.get(placeName(head))?.find((p) => parts.every((part) => within(p, part))) : undefined;
}

/**
 * Places for a picker query, best first: a label that starts with it, then another spelling or a code, then a later
 * word. A country's other spellings are mostly its regions, so they count only typed whole ("UK", "England").
 */
export function searchPlaces(query: string, limit = 8): Place[] {
  const q = placeName(query);
  if (!q) return [];
  const score = (p: Place) => {
    const label = placeName(p.label);
    const forms = p.kind === "country" ? p.forms.filter((f) => f === q) : p.forms;
    if (label.startsWith(q)) return 0;
    if (forms.some((f) => f.startsWith(q)) || p.codes.some((c) => c.toLowerCase() === q)) return 1;
    if (` ${label}`.includes(` ${q}`) || forms.some((f) => ` ${f}`.includes(` ${q}`))) return 2;
    return 3;
  };
  return PLACES.map((p, i) => ({ p, i, s: score(p) }))
    .filter((x) => x.s < 3)
    .sort((a, b) => a.s - b.s || a.p.rank - b.p.rank || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.p);
}
